import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import { VesselsAPI } from '../services/vessels.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';

const toneFor = (s: string): 'green' | 'amber' | 'slate' | 'blue' | 'red' => {
  if (s === 'in_stock' || s === 'active') return 'green';
  if (s === 'reserved' || s === 'maintenance') return 'amber';
  if (s === 'sold') return 'slate';
  if (s === 'exchanged') return 'blue';
  return 'slate';
};

const Vessels: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error } = useQuery('vessels', async () => VesselsAPI.list());

  const filtered = useMemo(() => {
    return (data || []).filter((item: any) => {
      const q = searchTerm.toLowerCase();
      const hay = `${item.make} ${item.model} ${item.registrationNumber}`.toLowerCase();
      const matchesSearch = !q || hay.includes(q);
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const totalValue = filtered.reduce((s: number, i: any) => s + Number(i.purchasePrice || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar embarcações.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Embarcações"
        title="Embarcações"
        subtitle="Inventário de embarcações"
        actions={
          hasPermission('assets.create') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/vessels/novo')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select className="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="in_stock">Em estoque</option>
              <option value="reserved">Reservado</option>
              <option value="sold">Vendido</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Nome ou código"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Embarcações</div>
          <div className="kpi-value">{filtered.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Valor estimado</div>
          <div className="kpi-value">{formatCurrency(totalValue)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Em estoque</div>
          <div className="kpi-value">
            {(data || []).filter((i: any) => i.status === 'in_stock').length}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Embarcações</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhum registro encontrado.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Embarcação</th>
                  <th className="hide-m">Registro</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item: any) => (
                  <tr key={item.id}>
                    <td>
                      <StackCell
                        initials={`${String(item.make || 'X')[0]}${String(item.model || 'Y')[0]}`.toUpperCase()}
                        title={`${item.make} ${item.model}`}
                        meta={`${item.year} · ${item.color || '—'}`}
                        stackExtra={
                          <>
                            {item.registrationNumber} · {formatStatus(item.status)} · {formatCurrency(Number(item.purchasePrice || 0))}
                          </>
                        }
                      />
                    </td>
                    <td className="mono hide-m">{item.registrationNumber}</td>
                    <td className="hide-m">
                      <StatusChip label={formatStatus(item.status)} tone={toneFor(item.status)} />
                    </td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(Number(item.purchasePrice || 0))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/vessels/${item.id}`)} />
                        {hasPermission('assets.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir Embarcação"
        message="Tem certeza que deseja excluir esta embarcação? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await VesselsAPI.delete(Number(deleteId));
            queryClient.invalidateQueries(['vessels']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Vessels;
